import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Sparkles, Award, RefreshCw, Trophy, Users, Keyboard, PenTool, X, AlertCircle } from 'lucide-react';
import confetti from 'canvas-confetti';
import { fetchActiveQuestions, recordWordAttempt, fetchSpellingWords, SpellingQuestion } from '../../services/spellingQuizService';
import { saveQuizRecord, fetchQuizRecords } from '../../services/quizRecordService';
import { fetchStudentsForTutee } from '../../services/tuteeService';
import { Tutee } from '../../types/tuition';

interface ScienceSpellingQuizProps {
  tutee: Tutee;
  onBack?: () => void;
}

/** New array in random order (does not mutate the source). */
function shuffleQuestions<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

const ScienceSpellingQuiz = ({ tutee, onBack }: ScienceSpellingQuizProps) => {
  // Fallback questions (used if no active questions in database)
  const rayneQuestionsFallback = [
    { sentence: "Towels can __________ water after a shower.", answer: "absorb", hint: "Soak up" },
    { sentence: "When you open a bottle of soda, it will __________ gas bubbles.", answer: "release", hint: "Let go" },
    { sentence: "A snake is a cold-blooded __________.", answer: "reptile", hint: "Group of animals" },
    { sentence: "The soil in the garden feels __________ after the rain.", answer: "moist", hint: "A little wet" },
    { sentence: "A __________ is so small that we cannot see it without a microscope.", answer: "microorganism", hint: "Micro + organism" },
    { sentence: "__________ grow on dead trees and help break them down.", answer: "fungi", hint: "Plural of fungus" },
    { sentence: "Bakers use __________ to make bread fluffy and soft.", answer: "yeast", hint: "Starts with Y" },
    { sentence: "Antarctica has some of the __________ temperatures on Earth.", answer: "coldest", hint: "Most cold" },
    { sentence: "Mount Everest has the __________ peak in the world.", answer: "highest", hint: "Most high" },
    { sentence: "We must __________ good hygiene to stay healthy.", answer: "practise", hint: "Do regularly" },
    { sentence: "Some dust particles can be seen with the __________.", answer: "microscope", hint: "Tool for seeing small things" },
    { sentence: "A __________ grows in damp forests and can be eaten.", answer: "mushroom", hint: "Type of fungus" },
    { sentence: "Old bread may grow __________ if left for too long.", answer: "mould", hint: "Fuzzy growth" },
    { sentence: "Living things can __________ to produce more of their kind.", answer: "reproduce", hint: "Make babies" },
    { sentence: "Sandpaper feels __________ compared to smooth glass.", answer: "rough", hint: "Not smooth" },
    { sentence: "Desert plants can __________ with little water.", answer: "survive", hint: "Stay alive" },
    { sentence: "A ruler can __________ slightly without breaking.", answer: "bend", hint: "Curve" },
    { sentence: "A soft sofa is very __________ to sit on.", answer: "comfortable", hint: "Feels nice" },
    { sentence: "Scientists are __________ the hardness of different rocks.", answer: "testing", hint: "Checking" },
    { sentence: "The __________ of the rope is tested by pulling on it.", answer: "strength", hint: "How strong" },
    { sentence: "Please __________ this box carefully with both hands.", answer: "handle", hint: "Hold and move" },
    { sentence: "The human __________ is an important sense organ.", answer: "eye", hint: "We see with it" },
    { sentence: "We take a __________ every few seconds to stay alive.", answer: "breath", hint: "Breathing in" },
    { sentence: "The __________ help us breathe in oxygen and breathe out carbon dioxide.", answer: "lungs", hint: "Body organs for breathing" },
    { sentence: "Iron is a type of __________.", answer: "metal", hint: "Material category" },
    { sentence: "Bottles are often made of __________ because it is light.", answer: "plastic", hint: "Man-made material" },
    { sentence: "Animals __________ to danger by running away or hiding.", answer: "respond", hint: "React" },
    { sentence: "Exercise will __________ your heartbeat.", answer: "increase", hint: "Make faster" },
    { sentence: "Resting will __________ your heartbeat.", answer: "decrease", hint: "Make slower" },
    { sentence: "Plants grow well __________ they get enough light and water.", answer: "when", hint: "At the time that" }
  ];

  const jeffreyQuestionsFallback = [
    { sentence: "Water is a __________ because it flows and takes the shape of its container.", answer: "liquid", hint: "State of matter that flows" },
    { sentence: "Ice is a __________ because it keeps its shape.", answer: "solid", hint: "State of matter with fixed shape" },
    { sentence: "The cheetah runs the __________ among all land animals.", answer: "fastest", hint: "Most fast" },
    { sentence: "The turtle runs the __________ among the three animals.", answer: "slowest", hint: "Most slow" },
    { sentence: "We use a __________ to measure how hot or cold something is.", answer: "thermometer", hint: "Temperature measuring tool" },
    { sentence: "The __________ in the classroom rose when the heater was turned on.", answer: "temperature", hint: "How hot or cold" },
    { sentence: "Wood is a common __________ used for making furniture.", answer: "material", hint: "What things are made of" },
    { sentence: "Draw a __________ line with your ruler.", answer: "straight", hint: "Not curved" },
    { sentence: "Magnets can __________ certain metals.", answer: "attract", hint: "Pull towards" },
    { sentence: "Two similar poles of a magnet will __________ each other.", answer: "repel", hint: "Push away" },
    { sentence: "Some solids are __________ dissolved in water.", answer: "easily", hint: "Without difficulty" },
    { sentence: "Glass is __________ so we can see through it.", answer: "transparent", hint: "See-through" },
    { sentence: "Frosted glass is __________ because it lets some light through.", answer: "translucent", hint: "Partly see-through" },
    { sentence: "A brick wall is __________ because light cannot pass through it.", answer: "opaque", hint: "Not see-through" },
    { sentence: "Exercise helps to increase muscle __________.", answer: "strength", hint: "How strong" },
    { sentence: "Food passes from the stomach into the __________ for digestion.", answer: "intestines", hint: "Long tube in body" },
    { sentence: "The __________ stores food and begins the digestion process.", answer: "stomach", hint: "Body part that holds food" },
    { sentence: "Turning up the heater will __________ the room temperature.", answer: "increase", hint: "Make higher" },
    { sentence: "Turning off the air conditioner will __________ the room temperature.", answer: "increase", hint: "Make higher" },
    { sentence: "The ball rolled __________ the two chairs.", answer: "between", hint: "In the middle of two things" },
    { sentence: "Lions and elephants are examples of __________.", answer: "mammals", hint: "Group of animals" },
    { sentence: "Plants need soil to __________ water and nutrients.", answer: "absorb", hint: "Soak up" },
    { sentence: "Salt is a type of __________ found in nature.", answer: "mineral", hint: "Natural substance" },
    { sentence: "We are __________ the length of the table using a measuring tape.", answer: "measuring", hint: "Finding the size" },
    { sentence: "A solid has a __________ shape that does not change.", answer: "fixed", hint: "Stays the same" },
    { sentence: "Mirrors __________ light to show our reflection.", answer: "reflect", hint: "Bounce back" }
  ];

  const [selectedStudent, setSelectedStudent] = useState<string | null>(null);
  const [questions, setQuestions] = useState<SpellingQuestion[]>([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState('');
  const [showHint, setShowHint] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [records, setRecords] = useState<Record<string, any[]>>({});
  const [timeLeft, setTimeLeft] = useState(30);
  const [timerActive, setTimerActive] = useState(false);
  const [inputMode, setInputMode] = useState<'keyboard' | 'handwriting'>('keyboard');
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentStroke, setCurrentStroke] = useState<{ x: number[]; y: number[]; t: number[] } | null>(null);
  const [allStrokes, setAllStrokes] = useState<any[]>([]);
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [wordIdMap, setWordIdMap] = useState<Map<string, string>>(new Map()); // Map answer -> wordId for statistics tracking
  const [quizStartTime, setQuizStartTime] = useState<Date | null>(null);
  const [questionsAttempted, setQuestionsAttempted] = useState<any[]>([]);
  const [quizResultWords, setQuizResultWords] = useState<
    { question: string; answer: string; userAnswer: string; isCorrect: boolean; attemptCount: number }[]
  >([]);
  const [studentNames, setStudentNames] = useState<string[]>([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(true);
  const [scoreBreakdownTapOpen, setScoreBreakdownTapOpen] = useState(false);
  const [scoreBreakdownHover, setScoreBreakdownHover] = useState(false);
  const [touchPrimaryNoHover, setTouchPrimaryNoHover] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(hover: none)');
    const sync = () => setTouchPrimaryNoHover(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Fetch students for this tutee
  useEffect(() => {
    const loadStudents = async () => {
      try {
        setIsLoadingStudents(true);
        const students = await fetchStudentsForTutee(tutee.id);
        const names = students.map(s => {
          // Capitalize first letter for display
          return s.studentName.charAt(0).toUpperCase() + s.studentName.slice(1);
        });
        setStudentNames(names);
      } catch (error) {
        console.error('Error loading students:', error);
        // Fallback to tutee name if no students found
        setStudentNames([tutee.name]);
      } finally {
        setIsLoadingStudents(false);
      }
    };

    loadStudents();
  }, [tutee.id, tutee.name]);

  // Prevent accidental page refresh/leave during active quiz
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (selectedStudent && questions.length > 0 && !completed) {
        e.preventDefault();
        e.returnValue = ''; // Required for some browsers
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [selectedStudent, questions.length, completed]);

  // Force light theme for quiz - ensure good contrast regardless of localStorage
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    return () => {
      // Don't restore theme on unmount - let ThemeContext handle it
    };
  }, []);

  useEffect(() => {
    const loadRecords = async () => {
      try {
        // Fetch records from Supabase for this tutee
        const supabaseRecords = await fetchQuizRecords(tutee.id);
        
        // Group records by student name
        const groupedRecords: Record<string, any[]> = {};
        studentNames.forEach(name => {
          const studentKey = name.toLowerCase();
          const filteredRecords = supabaseRecords.filter(r => r.studentName?.toLowerCase() === studentKey);
          
          groupedRecords[studentKey] = filteredRecords.map(r => ({
            date: r.createdAt ? new Date(r.createdAt).toLocaleString() : '',
            timestamp: r.createdAt || '',
            score: r.score,
            total: r.totalQuestions,
            percentage: r.percentage,
            timeSpent: r.timeSpent
          }));
        });
        
        setRecords(groupedRecords);
      } catch (error) {
        console.error('Error loading quiz records:', error);
        // Fallback to empty records
        const initialRecords: Record<string, any[]> = {};
        studentNames.forEach(name => {
          initialRecords[name.toLowerCase()] = [];
        });
        setRecords(initialRecords);
      }
    };
    
    loadRecords();
  }, [tutee.id, studentNames]);

  // Block text selection while answering (reduces accidental selection from palm / stylus on tablets)
  useEffect(() => {
    const active = Boolean(selectedStudent && questions.length > 0 && !completed);
    if (!active) return;
    const prevUserSelect = document.body.style.userSelect;
    const prevWebkitUserSelect = (document.body.style as CSSStyleDeclaration & { webkitUserSelect?: string }).webkitUserSelect;
    document.body.style.userSelect = 'none';
    (document.body.style as CSSStyleDeclaration & { webkitUserSelect?: string }).webkitUserSelect = 'none';
    return () => {
      document.body.style.userSelect = prevUserSelect;
      (document.body.style as CSSStyleDeclaration & { webkitUserSelect?: string }).webkitUserSelect = prevWebkitUserSelect ?? '';
    };
  }, [selectedStudent, questions.length, completed]);

  // Timer effect - countdown and auto-submit (30s total per question, not per attempt)
  useEffect(() => {
    // Only run timer when active and not showing final feedback
    const isFinalAnswer = showFeedback && (feedbackMsg.includes('Correct') || attempts >= 3);
    if (!timerActive || isFinalAnswer || completed || !selectedStudent || questions.length === 0) return;

    if (timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Time's up - force final answer
      setTimerActive(false);
      const currentAnswer = userAnswer.trim().toLowerCase();
      const correctAnswer = questions[currentQ].answer.trim().toLowerCase();
      const correct = currentAnswer === correctAnswer;
      
      const recordStats = async () => {
        if (selectedStudent && wordIdMap.size > 0) {
          const answer = questions[currentQ].answer.toLowerCase();
          const wordId = wordIdMap.get(answer);
          if (wordId) {
            try {
              await recordWordAttempt({
                wordId,
                tuteeId: tutee.id,
                studentName: selectedStudent,
                isCorrect: correct,
              });
            } catch (error) {
              console.error('Error recording word attempt:', error);
            }
          }
        }
      };
      
      if (currentAnswer && correct) {
        setFeedbackMsg('✓ Correct! Well done!');
        setShowFeedback(true);
        setScore(prev => prev + 1);
        setAttempts(0);
        recordStats();
      } else {
        // Time's up - show final answer regardless of attempts
        setAttempts(3); // Set to max attempts to show final answer
        setFeedbackMsg(`⏰ Time's up! The correct answer is: ${questions[currentQ].answer}`);
        setShowFeedback(true);
        recordStats();
      }
    }
  }, [timeLeft, timerActive, showFeedback, completed, selectedStudent, userAnswer, attempts, currentQ, questions, feedbackMsg, wordIdMap, tutee.id]);

  // Reset timer when question changes
  useEffect(() => {
    if (selectedStudent && questions.length > 0) {
      setTimeLeft(30);
      setTimerActive(true);
    }
  }, [currentQ, selectedStudent, questions.length]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && inputMode === 'handwriting') {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.strokeStyle = '#4c1d95';
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    }
  }, [inputMode]);

  useEffect(() => {
    if (completed) {
      const percentage = Math.round((score / questions.length) * 100);
      if (percentage >= 80) {
        // Simple celebration
        confetti({
          particleCount: 150,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#8b5cf6', '#3b82f6', '#ec4899', '#fbbf24']
        });

        // Extra celebration for perfect score
        if (percentage === 100) {
          setTimeout(() => {
            confetti({
              particleCount: 100,
              angle: 60,
              spread: 55,
              origin: { x: 0 },
              colors: ['#8b5cf6', '#3b82f6', '#ec4899', '#fbbf24']
            });
          }, 250);
          setTimeout(() => {
            confetti({
              particleCount: 100,
              angle: 120,
              spread: 55,
              origin: { x: 1 },
              colors: ['#8b5cf6', '#3b82f6', '#ec4899', '#fbbf24']
            });
          }, 400);
        }
      }
    }
  }, [completed, score, questions.length]);

  /** While feedback is visible, the submitted answer must not change (wand, typing, clear). */
  const answerLocked = showFeedback;

  // Handwriting canvas functions
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
    setAllStrokes([]);
    setCurrentStroke(null);
    if (!showFeedback) {
      setUserAnswer('');
    }
  };

  const getCanvasCoordinates = (e: React.TouchEvent<HTMLCanvasElement> | React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0, time: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
      time: Date.now()
    };
  };

  const startDrawing = (e: React.TouchEvent<HTMLCanvasElement> | React.MouseEvent<HTMLCanvasElement>) => {
    if (answerLocked) return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const { x, y, time } = getCanvasCoordinates(e);
    
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(x, y);
    }

    setCurrentStroke({ x: [x], y: [y], t: [time] });
  };

  const draw = (e: React.TouchEvent<HTMLCanvasElement> | React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const { x, y, time } = getCanvasCoordinates(e);
    
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.lineTo(x, y);
      ctx.strokeStyle = '#4c1d95';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    }

    if (currentStroke) {
      setCurrentStroke({
        x: [...currentStroke.x, x],
        y: [...currentStroke.y, y],
        t: [...currentStroke.t, time]
      });
    }
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    if (currentStroke) {
      setAllStrokes(prev => [...prev, [currentStroke.x, currentStroke.y, currentStroke.t]]);
      setCurrentStroke(null);
    }
  };

  const recognizeHandwriting = async () => {
    if (answerLocked || allStrokes.length === 0) return;

    try {
      setIsRecognizing(true);
      const response = await fetch('https://www.google.com.tw/inputtools/request?ime=handwriting&app=autraw&cs=1&oe=utf-8&itc=en-t-i0-handwrit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          options: 'enable_pre_space',
          requests: [
            {
              writing_guide: {
                writing_area_width: 600,
                writing_area_height: 200
              },
              pre_context: '',
              max_num_results: 3,
              max_candidates: 3,
              ink: allStrokes
            }
          ]
        })
      });

      const data = await response.json();
      if (data && data[0] === 'SUCCESS' && data[1][0][1][0]) {
        const recognizedText = data[1][0][1][0];
        setUserAnswer(recognizedText);
      }
    } catch (error) {
      console.error('Handwriting recognition failed:', error);
    } finally {
      setIsRecognizing(false);
    }
  };

  // Initialize canvas
  const selectStudent = async (studentName: string) => {
    const studentKey = studentName.toLowerCase();
    setSelectedStudent(studentKey);
    setQuizStartTime(new Date()); // Start tracking time
    setQuestionsAttempted([]); // Reset questions attempted
    setQuizResultWords([]);
    
    try {
      // Try to load active questions from database
      const activeQuestions = await fetchActiveQuestions(tutee.id, studentKey);
      
      if (activeQuestions && activeQuestions.length > 0) {
        // Random order per attempt (same set from DB, shuffled when you start)
        setQuestions(shuffleQuestions(activeQuestions));
        
        // Build word ID map for statistics tracking
        const words = await fetchSpellingWords(tutee.id, studentKey);
        const idMap = new Map<string, string>();
        words.forEach(word => {
          idMap.set(word.word.toLowerCase(), word.id);
        });
        setWordIdMap(idMap);
      } else {
        // Fallback to hardcoded questions for known students
        if (tutee.id === 'primary-school') {
          setQuestions(
            shuffleQuestions(studentKey === 'rayne' ? rayneQuestionsFallback : jeffreyQuestionsFallback)
          );
        } else {
          setQuestions([]);
        }
        setWordIdMap(new Map());
      }
    } catch (error) {
      console.error('Error loading questions:', error);
      // Fallback
      if (tutee.id === 'primary-school') {
        setQuestions(
          shuffleQuestions(studentKey === 'rayne' ? rayneQuestionsFallback : jeffreyQuestionsFallback)
        );
      } else {
        setQuestions([]);
      }
      setWordIdMap(new Map());
    }
  };

  const checkAnswer = async () => {
    if (showFeedback) return;

    const currentAnswer = userAnswer.trim().toLowerCase();
    const correctAnswer = questions[currentQ].answer.trim().toLowerCase();
    const correct = currentAnswer === correctAnswer;
    
    // Record statistics if we have word ID
    if (selectedStudent && wordIdMap.size > 0) {
      const answer = questions[currentQ].answer.toLowerCase();
      const wordId = wordIdMap.get(answer);
      if (wordId) {
        try {
          await recordWordAttempt({
            wordId,
            tuteeId: tutee.id,
            studentName: selectedStudent,
            isCorrect: correct,
          });
        } catch (error) {
          console.error('Error recording word attempt:', error);
        }
      }
    }
    
    if (correct) {
      // Correct answer - stop timer and show success
      setTimerActive(false);
      setFeedbackMsg('✓ Correct! Well done!');
      setShowFeedback(true);
      setScore(score + 1);
      setAttempts(0);
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      
      if (newAttempts >= 3) {
        // Final attempt - stop timer and record incorrect
        setTimerActive(false);
        setFeedbackMsg(`✗ The correct answer is: ${questions[currentQ].answer}`);
        setShowFeedback(true);
        
        // Record final incorrect attempt
        if (selectedStudent && wordIdMap.size > 0) {
          const answer = questions[currentQ].answer.toLowerCase();
          const wordId = wordIdMap.get(answer);
          if (wordId) {
            try {
              await recordWordAttempt({
                wordId,
                tuteeId: tutee.id,
                studentName: selectedStudent,
                isCorrect: false,
              });
            } catch (error) {
              console.error('Error recording word attempt:', error);
            }
          }
        }
      } else {
        // Wrong but can retry - timer continues, just show feedback
        setFeedbackMsg(`✗ Not quite right. You have ${3 - newAttempts} ${3 - newAttempts === 1 ? 'try' : 'tries'} left!`);
        setShowFeedback(true);
        setTimeout(() => {
          setShowFeedback(false);
          setUserAnswer('');
          // Timer continues running - don't reset it
        }, 1500);
      }
    }
  };

  const nextQuestion = () => {
    setTimerActive(false); // Stop current timer
    
    // Track this question before moving on
    const questionData = {
      question: questions[currentQ].sentence,
      answer: questions[currentQ].answer,
      userAnswer: userAnswer,
      isCorrect: userAnswer.trim().toLowerCase() === questions[currentQ].answer.trim().toLowerCase(),
      attemptCount: attempts + 1
    };
    setQuestionsAttempted(prev => [...prev, questionData]);
    
    if (currentQ < questions.length - 1) {
      setCurrentQ(currentQ + 1);
      setUserAnswer('');
      setShowFeedback(false);
      setShowHint(false);
      setAttempts(0);
      setFeedbackMsg('');
      setTimeLeft(30); // Reset timer for next question
      setTimerActive(true); // Start timer for next question
      clearCanvas(); // Clear handwriting canvas and strokes
    } else if (selectedStudent) {
      // Quiz completed! Calculate time and save
      const endTime = new Date();
      const timeSpentSeconds = quizStartTime ? Math.floor((endTime.getTime() - quizStartTime.getTime()) / 1000) : undefined;
      const percentage = Math.round((score / questions.length) * 100);
      
      // Track the final question
      const finalQuestionData = {
        question: questions[currentQ].sentence,
        answer: questions[currentQ].answer,
        userAnswer: userAnswer,
        isCorrect: userAnswer.trim().toLowerCase() === questions[currentQ].answer.trim().toLowerCase(),
        attemptCount: attempts + 1
      };
      const allQuestionsAttempted = [...questionsAttempted, finalQuestionData];
      setQuizResultWords(allQuestionsAttempted);

      // Update local state
      const newRecords = { ...records };
      const studentKey = selectedStudent;
      if (!newRecords[studentKey]) newRecords[studentKey] = [];
      const newRecord = {
        date: endTime.toLocaleString(),
        timestamp: endTime.toISOString(),
        score: score,
        total: questions.length,
        percentage: percentage,
        timeSpent: timeSpentSeconds
      };
      newRecords[studentKey].push(newRecord);
      setRecords(newRecords);
      
      // Save to Supabase with full data
      saveQuizRecord({
        tuteeId: tutee.id,
        studentName: selectedStudent,
        score: score,
        totalQuestions: questions.length,
        percentage: percentage,
        timeSpent: timeSpentSeconds,
        startTime: quizStartTime?.toISOString(),
        endTime: endTime.toISOString(),
        questionsAttempted: allQuestionsAttempted
      }).then(() => {
        console.log('Quiz record saved to Supabase successfully!');
      }).catch((error) => {
        console.error('Failed to save quiz record to Supabase:', error);
      });
      
      setScoreBreakdownTapOpen(false);
      setScoreBreakdownHover(false);
      setCompleted(true);
    }
  };

  const restart = () => {
    if (onBack) {
      onBack();
    } else {
      // Fallback if no callback provided
      setSelectedStudent(null);
      setCurrentQ(0);
      setUserAnswer('');
      setScore(0);
      setAttempts(0);
      setShowFeedback(false);
      setShowHint(false);
      setCompleted(false);
      setFeedbackMsg('');
      setQuizResultWords([]);
    }
  };

  useEffect(() => {
    setScoreBreakdownTapOpen(false);
  }, [currentQ]);

  const scoreBreakdownRows = useMemo(() => {
    type Row = { correct: boolean; submitted: string; actual: string };
    const rows: Row[] = questionsAttempted.map((q) => ({
      correct: q.isCorrect,
      submitted: (q.userAnswer || '').trim() || '—',
      actual: q.answer,
    }));
    const finalized =
      showFeedback && (feedbackMsg.includes('Correct') || attempts >= 3);
    if (finalized && questions[currentQ] && questionsAttempted.length === currentQ) {
      rows.push({
        correct: userAnswer.trim().toLowerCase() === questions[currentQ].answer.trim().toLowerCase(),
        submitted: userAnswer.trim() || '—',
        actual: questions[currentQ].answer,
      });
    }
    return rows;
  }, [questionsAttempted, showFeedback, feedbackMsg, attempts, questions, currentQ, userAnswer]);

  const scoreBreakdownOpen = touchPrimaryNoHover ? scoreBreakdownTapOpen : scoreBreakdownHover;

  const primaryColor = tutee.colorScheme.primary;
  const secondaryColor = tutee.colorScheme.secondary;
  const bgPrimary = `bg-${primaryColor}-600`;
  const textPrimary = `text-${primaryColor}-600`;
  const borderPrimary = `border-${primaryColor}-300`;
  const focusBorderPrimary = `focus:border-${primaryColor}-500`;
  const hoverBgPrimary = `hover:bg-${primaryColor}-700`;

  // Show loading state while fetching students
  if (isLoadingStudents) {
    return (
      <div className={`min-h-screen bg-gradient-to-br from-${primaryColor}-100 to-${secondaryColor}-100 p-4 sm:p-6 md:p-8 flex items-center justify-center safe-area-inset`}>
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl p-5 sm:p-6 md:p-8 max-w-md w-full text-center mx-2">
          <RefreshCw className={`w-16 h-16 ${textPrimary} mx-auto mb-4 animate-spin`} />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Loading Students...</h2>
          <p className="text-gray-600">Please wait while we fetch the student list.</p>
        </div>
      </div>
    );
  }

  if (!selectedStudent || questions.length === 0) {
    if (selectedStudent && questions.length === 0) {
      return (
        <div className={`min-h-screen bg-gradient-to-br from-${primaryColor}-100 to-${secondaryColor}-100 p-4 sm:p-6 md:p-8 flex items-center justify-center safe-area-inset`}>
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl p-5 sm:p-6 md:p-8 max-w-md w-full text-center mx-2">
            <AlertCircle className={`w-16 h-16 ${textPrimary} mx-auto mb-4`} />
            <h2 className="text-xl font-bold text-gray-800 mb-2">No Quiz Questions Available</h2>
            <p className="text-gray-600 mb-4">Words have been added, but quiz questions haven't been generated yet.</p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-left">
              <p className="text-sm font-bold text-blue-800 mb-2">📝 To generate questions:</p>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                <li>Go to Admin → Spelling Quiz Manager</li>
                <li>Select {selectedStudent.charAt(0).toUpperCase() + selectedStudent.slice(1)}</li>
                <li>Click the "AI Questions" button</li>
                <li>Review and confirm the generated questions</li>
              </ol>
            </div>
            
            <button 
              onClick={() => {
                setSelectedStudent(null);
                if (onBack) onBack();
              }}
              className={`w-full px-6 py-3 bg-gradient-to-r from-${primaryColor}-600 to-${secondaryColor}-600 text-white rounded-xl font-bold hover:opacity-90 transition-all`}
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className={`min-h-screen bg-gradient-to-br from-${primaryColor}-100 to-${secondaryColor}-100 p-4 sm:p-6 md:p-8 flex items-center justify-center safe-area-inset`}>
        <div className="bg-white rounded-[2rem] sm:rounded-2xl shadow-xl sm:shadow-2xl p-5 sm:p-6 md:p-8 max-w-2xl w-full mx-2">
          <div className="text-center mb-6 sm:mb-8">
            <Users className={`w-10 h-10 sm:w-16 sm:h-16 ${textPrimary} mx-auto mb-2 sm:mb-4`} />
            <h1 className={`text-xl sm:text-3xl md:text-4xl font-bold text-${primaryColor}-700 mb-1 sm:mb-2 px-2`}>Science Spelling Quiz</h1>
            <p className="text-xs sm:text-base text-gray-600 px-2 font-medium">Choose your student to start!</p>
          </div>

          <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {studentNames.map((name, idx) => {
              const key = name.toLowerCase();
              const studentRecord = records[key] || [];
              const bestScore = studentRecord.length > 0 ? Math.max(...studentRecord.map(r => r.percentage)) : 0;
              const isPink = idx % 2 === 0;
              
              return (
                <button
                  key={name}
                  onClick={() => selectStudent(name)}
                  className={`bg-gradient-to-br ${isPink ? 'from-pink-400 to-purple-500' : 'from-blue-400 to-indigo-500'} text-white p-5 sm:p-6 md:p-8 rounded-lg sm:rounded-xl active:scale-95 transition-transform shadow-lg min-h-[140px] touch-manipulation text-left`}
                >
                  <h2 className="text-2xl sm:text-3xl font-bold mb-2">{name}'s Quiz</h2>
                  <p className={`mb-3 sm:mb-4 text-sm sm:text-base ${isPink ? 'text-pink-100' : 'text-blue-100'}`}>
                    Customized for your level
                  </p>
                  {studentRecord.length > 0 && (
                    <div className="bg-white/20 rounded-lg p-2 sm:p-3 text-xs sm:text-sm">
                      <p className="font-semibold">Best Score:</p>
                      <p className="text-xl sm:text-2xl">{bestScore}%</p>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {Object.values(records).some(r => r.length > 0) && (
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg sm:rounded-xl p-4 sm:p-6 border-2 border-yellow-300">
              <div className="flex items-center gap-2 mb-4 sm:mb-6">
                <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500 flex-shrink-0" />
                <h3 className={`text-lg sm:text-xl md:text-2xl font-bold text-${primaryColor}-700`}>🏆 Recent Performance</h3>
              </div>
              
              <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                {studentNames.map((name, idx) => {
                  const key = name.toLowerCase();
                  const studentRecord = records[key] || [];
                  const bestScore = studentRecord.length > 0 ? Math.max(...studentRecord.map(r => r.percentage)) : 0;
                  const isPink = idx % 2 === 0;
                  
                  if (studentRecord.length === 0) return null;

                  return (
                    <div key={name} className={`bg-white rounded-lg p-3 sm:p-4 border-2 ${isPink ? 'border-pink-300' : 'border-blue-300'}`}>
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <h4 className={`font-bold ${isPink ? 'text-pink-600' : 'text-blue-600'} text-base sm:text-lg`}>{name}</h4>
                        <span className={`text-xl sm:text-2xl font-bold ${isPink ? 'text-pink-600' : 'text-blue-600'}`}>
                          {bestScore}%
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">Best Score</p>
                      <p className="text-xs text-gray-500">Total Attempts: {studentRecord.length}</p>
                      <div className="mt-2 text-xs text-gray-600">
                        Latest: {studentRecord[studentRecord.length - 1].percentage}%
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (completed) {
    const percentage = Math.round((score / questions.length) * 100);
    const resultRows = quizResultWords.map((q) => ({
      correct: q.isCorrect,
      submitted: (q.userAnswer || '').trim() || '—',
      actual: q.answer,
    }));

    return (
      <div className={`min-h-screen bg-gradient-to-br from-${primaryColor}-100 to-${secondaryColor}-100 p-4 sm:p-6 md:p-8 flex items-center justify-center safe-area-inset select-none`}>
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl p-5 sm:p-6 md:p-8 max-w-md w-full text-center mx-2">
          <Award className="w-16 h-16 sm:w-20 sm:h-20 text-yellow-500 mx-auto mb-3 sm:mb-4" />
          <h2 className={`text-2xl sm:text-3xl font-bold mb-2 px-2 text-${primaryColor}-600`}>
            {selectedStudent === 'rayne' ? 'Rayne' : selectedStudent === 'jeffrey' ? 'Jeffrey' : selectedStudent}'s Result
          </h2>
          <div
            className="relative z-20 mx-auto mb-2 inline-block text-center"
            onMouseEnter={() => {
              if (!touchPrimaryNoHover) setScoreBreakdownHover(true);
            }}
            onMouseLeave={() => {
              if (!touchPrimaryNoHover) setScoreBreakdownHover(false);
            }}
          >
            <button
              type="button"
              className="text-5xl sm:text-6xl font-bold text-gray-700 border-b-2 border-dashed border-gray-400/50 pb-0.5 hover:border-gray-500 transition-colors"
              onClick={() => {
                if (touchPrimaryNoHover) setScoreBreakdownTapOpen((o) => !o);
              }}
              aria-expanded={scoreBreakdownOpen}
              aria-haspopup="true"
              aria-label="Score and word recap"
            >
              {score}/{questions.length}
            </button>
            {scoreBreakdownOpen && (
              <div
                role="tooltip"
                className="absolute left-1/2 top-full z-30 mt-3 w-[min(calc(100vw-2rem),20rem)] -translate-x-1/2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left shadow-xl ring-1 ring-black/5"
              >
                <p className="border-b border-gray-100 pb-2 mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">Your words</p>
                {resultRows.length === 0 ? (
                  <p className="text-sm text-gray-500">Word list unavailable for this result.</p>
                ) : (
                  <ul className="max-h-52 space-y-2 overflow-y-auto text-sm">
                    {resultRows.map((row, i) => (
                      <li key={i} className={row.correct ? 'text-green-700' : 'text-red-800'}>
                        {row.correct ? (
                          <span className="font-semibold">{row.actual}</span>
                        ) : (
                          <>
                            <span className="text-gray-500 line-through decoration-red-400/70">{row.submitted}</span>
                            <span className="mx-1.5 text-gray-400">→</span>
                            <span className="font-semibold">{row.actual}</span>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <p className={`text-3xl sm:text-4xl font-bold mb-4 sm:mb-6 text-${primaryColor}-600`}>
            {percentage}%
          </p>
          <div className="mb-4 sm:mb-6 px-2">
            {percentage === 100 && <p className="text-lg sm:text-xl text-green-600">🌟 Perfect Score! You're a spelling superstar!</p>}
            {percentage >= 80 && percentage < 100 && <p className="text-lg sm:text-xl text-blue-600">🎉 Excellent work! Keep it up!</p>}
            {percentage >= 60 && percentage < 80 && <p className={`text-lg sm:text-xl text-${primaryColor}-600`}>👍 Good job! Practice makes perfect!</p>}
            {percentage < 60 && <p className="text-lg sm:text-xl text-orange-600">💪 Keep practicing! You're improving!</p>}
          </div>
          <button
            onClick={restart}
            className={`${bgPrimary} text-white px-6 sm:px-8 py-3 rounded-lg text-base sm:text-lg font-semibold hover:${hoverBgPrimary.replace('hover:bg-', 'hover:bg-')} active:scale-95 transition-all flex items-center gap-2 mx-auto min-h-[44px] touch-manipulation`}
          >
            <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
            Back to Menu
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br from-${primaryColor}-50 to-${secondaryColor}-50 p-4 sm:p-6 md:p-8 flex items-center justify-center safe-area-inset select-none`}>
      <div className="bg-white rounded-[2rem] sm:rounded-2xl shadow-xl sm:shadow-2xl p-4 sm:p-6 md:p-8 max-w-2xl w-full mx-2">
        <div className="flex flex-row justify-between items-center gap-3 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              {/* Circular Timer - Left Side (Clickable to add 15 seconds) */}
              {timerActive && !(showFeedback && (feedbackMsg.includes('Correct') || attempts >= 3)) && (
                <button
                  onClick={() => {
                    setTimeLeft(prev => prev + 15);
                  }}
                  className="relative w-10 h-10 sm:w-14 sm:h-14 md:w-16 md:h-16 flex-shrink-0 cursor-pointer hover:scale-110 active:scale-95 transition-transform group"
                  title="Click to add 15 seconds"
                >
                  <svg className="transform -rotate-90 w-10 h-10 sm:w-14 sm:h-14 md:w-16 md:h-16" viewBox="0 0 64 64">
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                      className="text-gray-200"
                    />
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 28}`}
                      strokeDashoffset={`${2 * Math.PI * 28 * (1 - timeLeft / 30)}`}
                      className={`transition-all duration-1000 ${
                        timeLeft > 20 ? 'text-green-500' :
                        timeLeft > 10 ? 'text-yellow-500' :
                        'text-red-500'
                      }`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className={`text-xs sm:text-base md:text-lg font-bold ${
                      timeLeft > 20 ? 'text-green-600' :
                      timeLeft > 10 ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {timeLeft}
                    </span>
                  </div>
                  {/* Hover indicator */}
                  <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    <span className="text-[8px] sm:text-[10px] text-gray-500 font-bold">+15s</span>
                  </div>
                </button>
              )}
              <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                <Sparkles className={`w-4 h-4 sm:w-6 sm:h-6 flex-shrink-0 ${textPrimary}`} />
                <h1 className={`text-base sm:text-xl md:text-2xl font-bold truncate ${textPrimary}`}>
                  {selectedStudent === 'rayne' ? 'Rayne' : selectedStudent === 'jeffrey' ? 'Jeffrey' : selectedStudent}'s Quiz
                </h1>
              </div>
            </div>
            <div
              className="relative flex-shrink-0 text-right z-20"
              onMouseEnter={() => {
                if (!touchPrimaryNoHover) setScoreBreakdownHover(true);
              }}
              onMouseLeave={() => {
                if (!touchPrimaryNoHover) setScoreBreakdownHover(false);
              }}
            >
              <p className="text-[10px] sm:text-sm text-gray-600 font-bold uppercase tracking-wider">Q {currentQ + 1}/{questions.length}</p>
              <button
                type="button"
                className={`text-sm sm:text-lg font-black ${textPrimary} border-b border-dashed border-current/35 pb-0.5 hover:border-current/60 transition-colors`}
                onClick={() => {
                  if (touchPrimaryNoHover) setScoreBreakdownTapOpen((o) => !o);
                }}
                aria-expanded={scoreBreakdownOpen}
                aria-haspopup="true"
                aria-label="Score; hover or tap for word recap"
              >
                Score: {score}
              </button>
              {scoreBreakdownOpen && (
                <div
                  role="tooltip"
                  className="absolute right-0 top-full mt-2 w-[min(calc(100vw-2rem),18rem)] rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left shadow-xl ring-1 ring-black/5"
                >
                  <p className="border-b border-gray-100 pb-2 mb-2 text-[10px] font-bold uppercase tracking-wide text-gray-500 sm:text-xs">Words so far</p>
                  {scoreBreakdownRows.length === 0 ? (
                    <p className="text-xs text-gray-500 sm:text-sm">
                      When a question is finished (correct, out of tries, or time up), its word shows up here.
                    </p>
                  ) : (
                    <ul className="max-h-52 space-y-2 overflow-y-auto text-xs sm:text-sm">
                      {scoreBreakdownRows.map((row, i) => (
                        <li key={i} className={row.correct ? 'text-green-700' : 'text-red-800'}>
                          {row.correct ? (
                            <span className="font-semibold">{row.actual}</span>
                          ) : (
                            <>
                              <span className="text-gray-500 line-through decoration-red-400/70">{row.submitted}</span>
                              <span className="mx-1.5 text-gray-400">→</span>
                              <span className="font-semibold">{row.actual}</span>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mb-6">
            <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
              <div 
                className="h-3 rounded-full transition-all duration-300"
                style={{ 
                  width: `${((currentQ + 1) / questions.length) * 100}%`,
                  background: `linear-gradient(to right, ${tutee.colorScheme.primary === 'pink' ? '#ec4899' : tutee.colorScheme.primary === 'green' ? '#10b981' : tutee.colorScheme.primary === 'blue' ? '#3b82f6' : '#6366f1'}, ${tutee.colorScheme.secondary === 'purple' ? '#a855f7' : tutee.colorScheme.secondary === 'teal' ? '#14b8a6' : tutee.colorScheme.secondary === 'indigo' ? '#4f46e5' : '#8b5cf6'})`
                }}
              ></div>
            </div>
          </div>

          <div className={`bg-${primaryColor}-50 rounded-lg sm:rounded-xl p-4 sm:p-6 mb-4 sm:mb-6`}>
            <p className="text-base sm:text-lg md:text-xl text-gray-800 leading-relaxed">
              {questions[currentQ].sentence}
            </p>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {/* Input Mode Toggle */}
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => setInputMode('keyboard')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  inputMode === 'keyboard'
                    ? `${bgPrimary} text-white`
                    : `bg-gray-100 text-gray-700 hover:bg-gray-200`
                } min-h-[44px] touch-manipulation`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Keyboard className="w-4 h-4" />
                  <span>Keyboard</span>
                </div>
              </button>
              <button
                onClick={() => {
                  setInputMode('handwriting');
                  clearCanvas();
                }}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  inputMode === 'handwriting'
                    ? `${bgPrimary} text-white`
                    : `bg-gray-100 text-gray-700 hover:bg-gray-200`
                } min-h-[44px] touch-manipulation`}
              >
                <div className="flex items-center justify-center gap-2">
                  <PenTool className="w-4 h-4" />
                  <span>Handwriting</span>
                </div>
              </button>
            </div>

            {/* Keyboard Input Mode */}
            {inputMode === 'keyboard' && (
              <input
                type="text"
                value={userAnswer}
                onChange={(e) => setUserAnswer(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && userAnswer && !showFeedback && checkAnswer()}
                placeholder="Type your answer here..."
                className={`w-full select-text px-4 py-3.5 sm:py-3 text-base sm:text-lg text-gray-900 !text-gray-900 bg-white !bg-white border-2 ${borderPrimary} rounded-lg ${focusBorderPrimary} outline-none min-h-[44px] touch-manipulation dark:!text-gray-900 dark:!bg-white placeholder:text-gray-500 dark:placeholder:text-gray-500`}
                autoFocus
                disabled={answerLocked}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                inputMode="text"
              />
            )}

            {/* Handwriting Canvas Mode */}
            {inputMode === 'handwriting' && (
              <div className="space-y-2">
                <div className={`bg-white border-2 ${borderPrimary} rounded-lg p-2 relative`}>
                  <canvas
                    ref={canvasRef}
                    width={600}
                    height={200}
                    className={`w-full h-32 sm:h-40 md:h-48 border border-gray-200 rounded touch-none ${answerLocked ? 'pointer-events-none opacity-60' : ''}`}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    style={{ touchAction: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={clearCanvas}
                    disabled={answerLocked}
                    className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 active:scale-95 transition-all touch-manipulation disabled:bg-red-300 disabled:cursor-not-allowed"
                    aria-label="Clear drawing"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex justify-between items-center gap-4">
                  <p className="text-xs sm:text-sm text-blue-800 flex-1">
                    💡 <strong>Tip:</strong> Draw your answer and tap the wand to convert to text!
                  </p>
                  <button
                    type="button"
                    onClick={recognizeHandwriting}
                    disabled={answerLocked || isRecognizing || allStrokes.length === 0}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-bold text-sm hover:bg-blue-700 transition-all disabled:bg-blue-300 disabled:cursor-not-allowed shadow-md press-effect shrink-0"
                  >
                    {isRecognizing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    <span>Magic Wand</span>
                  </button>
                </div>
                <input
                  type="text"
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && userAnswer && !showFeedback && checkAnswer()}
                  placeholder="The Magic Wand will type your drawing here..."
                  className={`w-full select-text px-4 py-3.5 sm:py-3 text-base sm:text-lg text-gray-900 !text-gray-900 bg-white !bg-white border-2 ${borderPrimary} rounded-lg ${focusBorderPrimary} outline-none min-h-[44px] touch-manipulation dark:!text-gray-900 dark:!bg-white placeholder:text-gray-500 dark:placeholder:text-gray-500`}
                  disabled={answerLocked}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  inputMode="text"
                />
              </div>
            )}
            
            {showFeedback && (
              <div className={`rounded-xl p-4 ${
                feedbackMsg.includes('Correct') ? 'bg-green-50 border-2 border-green-300' : 
                attempts >= 3 ? 'bg-red-50 border-2 border-red-300' : 'bg-orange-50 border-2 border-orange-300'
              }`}>
                <p className={`font-bold ${
                  feedbackMsg.includes('Correct') ? 'text-green-700' : 
                  attempts >= 3 ? 'text-red-700' : 'text-orange-700'
                }`}>
                  {feedbackMsg}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              {(!showFeedback || (showFeedback && attempts < 3 && !feedbackMsg.includes('Correct'))) && (
                <button
                  onClick={checkAnswer}
                  disabled={!userAnswer}
                  className={`flex-1 text-white px-4 sm:px-6 py-3.5 sm:py-3 rounded-lg text-base sm:text-lg font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed active:scale-95 transition-all min-h-[44px] touch-manipulation ${bgPrimary} hover:${hoverBgPrimary.replace('hover:bg-', 'hover:bg-')}`}
                >
                  Check Answer
                </button>
              )}
              
              {(showFeedback && (feedbackMsg.includes('Correct') || attempts >= 3)) && (
                <button
                  onClick={nextQuestion}
                  className="flex-1 bg-green-600 text-white px-4 sm:px-6 py-3.5 sm:py-3 rounded-lg text-base sm:text-lg font-semibold hover:bg-green-700 active:scale-95 transition-all min-h-[44px] touch-manipulation"
                >
                  {currentQ < questions.length - 1 ? 'Next Question →' : 'See Results'}
                </button>
              )}
              
              <button
                onClick={() => setShowHint(!showHint)}
                className="px-4 sm:px-6 py-3.5 sm:py-3 bg-yellow-500 text-white rounded-lg font-semibold hover:bg-yellow-600 active:scale-95 transition-all min-h-[44px] touch-manipulation"
              >
                {showHint ? 'Hide' : 'Hint'}
              </button>
            </div>

            {showHint && (
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                <p className="text-yellow-800">💡 <strong>Hint:</strong> {questions[currentQ].hint}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
};

export default ScienceSpellingQuiz;

